/**
 * §7.12 Contact Sales — enterprise procurement CTA card for buyers
 * requiring custom deals (procurement, partnerships, multi-licence).
 */

import React from 'react'
import {
  Handshake, Building2, Globe, Boxes, Layers, ArrowRight, Sparkles,
} from 'lucide-react'
import {
  Container, Eyebrow, SectionHeading, GradientButton, GhostButton,
} from '@/components/landing/design-system'

const REASONS = [
  { i: Building2, l: 'Enterprise procurement',        d: 'Formal RFPs, security reviews and vendor onboarding.' },
  { i: Handshake,  l: 'Strategic partnerships',        d: 'Co-marketing, revenue share and reseller programmes.' },
  { i: Globe,     l: 'Global deployments',             d: 'Multi-region hosting, data residency, localisation.' },
  { i: Layers,    l: 'Custom commercial discussions',  d: 'Bespoke SLAs, extended terms, dedicated hosting.' },
  { i: Boxes,     l: 'Multi-license opportunities',    d: 'Volume-tier licensing across multiple business units.' },
]

export default function ContactSales() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Contact Sales</Eyebrow>
          <SectionHeading align="center"
            title={<>Enterprise procurement{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">and strategic partnerships.</span></>}
            subtitle="For organisations requiring bespoke commercial arrangements beyond the three standard licences — we work with your procurement, legal and security teams end-to-end."
          />
        </div>

        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-5 md:p-8 backdrop-blur-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-70"
            style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.10), transparent 70%)' }} />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Reasons */}
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {REASONS.map((r) => (
                <div key={r.l} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-brand-gold/35 bg-brand-gold/[0.08] text-brand-gold">
                      <r.i strokeWidth={1.8} style={{ width: 14, height: 14 }} />
                    </span>
                    <span className="text-[13.5px] font-semibold text-white leading-tight">{r.l}</span>
                  </div>
                  <p className="text-[12px] text-zinc-400 leading-relaxed">{r.d}</p>
                </div>
              ))}
            </div>

            {/* CTA card */}
            <div className="lg:col-span-4">
              <div className="relative rounded-2xl border border-brand-gold/30 bg-brand-gold/[0.06] p-5 flex flex-col gap-4 h-full">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-brand-gold" />
                  <span className="text-[10.5px] font-mono uppercase tracking-wider text-brand-gold">Enterprise desk</span>
                </div>
                <div className="text-[15px] font-semibold text-white leading-snug">
                  Talk to a specialist who can architect the commercial deal around your business.
                </div>
                <ul className="flex flex-col gap-1.5 text-[12.5px] text-zinc-300">
                  <li className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-brand-gold" /> 30-minute discovery call</li>
                  <li className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-brand-gold" /> Security &amp; compliance packet</li>
                  <li className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-brand-gold" /> Custom commercial proposal</li>
                </ul>
                <div className="mt-auto flex flex-col gap-2">
                  <GradientButton href="/contact" className="w-full justify-center">
                    <Handshake className="h-4 w-4" /> Contact sales
                  </GradientButton>
                  <GhostButton href="/get-started" trailingIcon className="w-full justify-center">
                    Or self-serve <ArrowRight className="h-3 w-3" />
                  </GhostButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
